// src/constants/templates.ts
import { CardTemplate, RecipientType } from "../types/card.types";
import { ArchetypeId } from "./archetypes";

// Create a complete Record with all RecipientType values
export const CARD_TEMPLATES: Record<RecipientType, CardTemplate> = {
  // Romantic
  partner: {
    id: "template-romantic-partner",
    recipientType: "partner",
    archetype: "heartbeat",
    name: "Forever Yours",
    description: "A timeless expression of love for your significant other",
    thumbnail:
      "https://images.pexels.com/photos/1024960/pexels-photo-1024960.jpeg",
    preview: {
      title: "To My Love",
      message: "Every moment with you feels like a beautiful dream.",
      personalNote: "You're my favorite hello and my hardest goodbye.",
      memory: "The day we met - the day my life truly began.",
      insideJoke:
        "Remember when we tried to cook together? Best disaster ever. ❤️",
    },
    theme: {
      id: "romantic-rose",
      primaryColor: "#ff6b6b",
      secondaryColor: "#feca57",
      accentColor: "#ff9ff3",
      fontFamily: "Quicksand",
    },
    effects: {
      loadingScreen: "romantic",
      proposalAnimation: true,
      letterStyle: "heartfelt",
    },
  },

  spouse: {
    id: "template-romantic-spouse",
    recipientType: "spouse",
    archetype: "heartbeat",
    name: "Until Forever",
    description: "Celebrate your marriage and beautiful journey",
    thumbnail:
      "https://images.pexels.com/photos/1150123/pexels-photo-1150123.jpeg",
    preview: {
      title: "My Beloved",
      message: "Through every season, you've been my constant.",
      personalNote: "You're not just my spouse; you're my home.",
      memory:
        "Our wedding day was perfect, but every day since has been even better.",
      insideJoke:
        "Remember when you locked us out of the house? That was the day I knew. 😂",
    },
    theme: {
      id: "spouse-elegant",
      primaryColor: "#b83b5e",
      secondaryColor: "#6a2c70",
      accentColor: "#f08a5d",
      fontFamily: "Georgia",
    },
    effects: {
      loadingScreen: "luxury",
      proposalAnimation: true,
      letterStyle: "elegant",
    },
  },

  crush: {
    id: "template-romantic-crush",
    recipientType: "crush",
    archetype: "heartbeat",
    name: "Secret Feelings",
    description: "Express your admiration in a playful way",
    thumbnail:
      "https://images.pexels.com/photos/3769021/pexels-photo-3769021.jpeg",
    preview: {
      title: "Hey You",
      message: "I've been trying to find the right words...",
      personalNote: "I smile every time I see you.",
      memory: "That time you laughed at my joke? I think about it.",
      insideJoke: "I pretend I don't notice you, but I always do. 👀",
    },
    theme: {
      id: "crush-playful",
      primaryColor: "#ff9ff3",
      secondaryColor: "#feca57",
      accentColor: "#ff6b6b",
      fontFamily: "Quicksand",
    },
    effects: {
      loadingScreen: "playful",
      proposalAnimation: false,
      letterStyle: "playful",
    },
  },

  secret_admirer: {
    id: "template-romantic-secret",
    recipientType: "secret_admirer",
    archetype: "heartbeat",
    name: "Secret Admirer",
    description: "Send love anonymously",
    thumbnail:
      "https://images.pexels.com/photos/761977/pexels-photo-761977.jpeg",
    preview: {
      title: "Shh... It's a Secret",
      message: "Someone out there is thinking of you...",
      personalNote: "You have a secret admirer.",
      memory: "The mystery is part of the magic.",
      insideJoke: "Can you guess who? 🤫",
    },
    theme: {
      id: "secret-mystery",
      primaryColor: "#6c5ce7",
      secondaryColor: "#a363d9",
      accentColor: "#e84393",
      fontFamily: "Montserrat",
    },
    effects: {
      loadingScreen: "romantic",
      proposalAnimation: false,
      letterStyle: "poetic",
    },
  },

  // Family
  mother: {
    id: "template-family-mother",
    recipientType: "mother",
    archetype: "heartbeat",
    name: "Grateful Heart",
    description: "Show your mom how much she means to you",
    thumbnail:
      "https://images.pexels.com/photos/733475/pexels-photo-733475.jpeg",
    preview: {
      title: "To Mom",
      message: "Every good thing in me came from you.",
      personalNote: "You're my first friend and forever hero.",
      memory: "All those nights you stayed up with me when I was sick.",
      insideJoke: 'Remember that time I "helped" you bake? 😅',
    },
    theme: {
      id: "mother-warm",
      primaryColor: "#ffafbd",
      secondaryColor: "#ffc3a0",
      accentColor: "#d4a5a5",
      fontFamily: "Georgia",
    },
    effects: {
      loadingScreen: "romantic",
      proposalAnimation: false,
      letterStyle: "heartfelt",
    },
  },

  father: {
    id: "template-family-father",
    recipientType: "father",
    archetype: "heartbeat",
    name: "Strength & Honor",
    description: "Celebrate your father's guidance",
    thumbnail:
      "https://images.pexels.com/photos/4261781/pexels-photo-4261781.jpeg",
    preview: {
      title: "To Dad",
      message: "You taught me how to be strong and kind.",
      personalNote: "I'm proud to be your child.",
      memory: "Early mornings you took me to practice before work.",
      insideJoke: "Remember when you tried to fix the sink? 🛠️",
    },
    theme: {
      id: "father-strong",
      primaryColor: "#6a89cc",
      secondaryColor: "#b8e994",
      accentColor: "#78e08f",
      fontFamily: "Montserrat",
    },
    effects: {
      loadingScreen: "luxury",
      proposalAnimation: false,
      letterStyle: "elegant",
    },
  },

  parents: {
    id: "template-family-parents",
    recipientType: "parents",
    archetype: "heartbeat",
    name: "Thank You",
    description: "For both mom and dad",
    thumbnail:
      "https://images.pexels.com/photos/6238131/pexels-photo-6238131.jpeg",
    preview: {
      title: "To Mom & Dad",
      message: "Thank you for building my world with love.",
      personalNote: "I'm who I am because of you both.",
      memory: "Family dinners, holidays, and everything in between.",
      insideJoke: "Remember all those family road trips? 🚗",
    },
    theme: {
      id: "parents-warm",
      primaryColor: "#ccae62",
      secondaryColor: "#e1b382",
      accentColor: "#c39a5b",
      fontFamily: "Georgia",
    },
    effects: {
      loadingScreen: "luxury",
      proposalAnimation: false,
      letterStyle: "elegant",
    },
  },

  sibling: {
    id: "template-family-sibling",
    recipientType: "sibling",
    archetype: "heartbeat",
    name: "Partner in Crime",
    description: "For your brother or sister",
    thumbnail:
      "https://images.pexels.com/photos/8129437/pexels-photo-8129437.jpeg",
    preview: {
      title: "To My Sibling",
      message: "We fought, we laughed, we grew. You were always my anchor.",
      personalNote: "Thanks for keeping all my secrets.",
      memory: "Building forts and thinking we were invincible.",
      insideJoke: "Remember when you blamed me for the broken vase? 😂",
    },
    theme: {
      id: "sibling-fun",
      primaryColor: "#f6b93b",
      secondaryColor: "#e55039",
      accentColor: "#78e08f",
      fontFamily: "Quicksand",
    },
    effects: {
      loadingScreen: "playful",
      proposalAnimation: false,
      letterStyle: "playful",
    },
  },

  extended_family: {
    id: "template-family-extended",
    recipientType: "extended_family",
    archetype: "heartbeat",
    name: "Family Love",
    description: "For grandparents, aunts, uncles, cousins",
    thumbnail:
      "https://images.pexels.com/photos/7524717/pexels-photo-7524717.jpeg",
    preview: {
      title: "To My Family",
      message: "Grateful for the love that connects us all.",
      personalNote: "Family isn't just important, it's everything.",
      memory: "Holiday gatherings filled with laughter and love.",
      insideJoke: "Our family is chaotic and I wouldn't change it. ❤️",
    },
    theme: {
      id: "family-warm",
      primaryColor: "#b8a9c9",
      secondaryColor: "#d4c2d1",
      accentColor: "#c2a2b0",
      fontFamily: "Georgia",
    },
    effects: {
      loadingScreen: "romantic",
      proposalAnimation: false,
      letterStyle: "heartfelt",
    },
  },

  child: {
    id: "template-family-child",
    recipientType: "child",
    archetype: "heartbeat",
    name: "To My Child",
    description: "A message of love for your child",
    thumbnail:
      "https://images.pexels.com/photos/1128318/pexels-photo-1128318.jpeg",
    preview: {
      title: "My Dear Child",
      message: "You are my greatest joy and proudest achievement.",
      personalNote: "Never forget how much you are loved.",
      memory: "The day you were born changed my world forever.",
      insideJoke: "You'll always be my baby, no matter how old you get. 👶",
    },
    theme: {
      id: "child-warm",
      primaryColor: "#ffb3ba",
      secondaryColor: "#ffdfba",
      accentColor: "#ffffba",
      fontFamily: "Quicksand",
    },
    effects: {
      loadingScreen: "playful",
      proposalAnimation: false,
      letterStyle: "heartfelt",
    },
  },

  // Friends
  best_friend: {
    id: "template-friend-best",
    recipientType: "best_friend",
    archetype: "heartbeat",
    name: "Soul Sister/Brother",
    description: "For your person, your ride or die",
    thumbnail:
      "https://images.pexels.com/photos/1029635/pexels-photo-1029635.jpeg",
    preview: {
      title: "To My Best Friend",
      message: "You're the family I chose, and I'd choose you every time.",
      personalNote: "Thanks for knowing me better than I know myself.",
      memory: "All those late night calls when I needed you.",
      insideJoke: "Remember that trip where everything went wrong? ✈️",
    },
    theme: {
      id: "friendship-forever",
      primaryColor: "#7ed6df",
      secondaryColor: "#e056fd",
      accentColor: "#badc58",
      fontFamily: "Quicksand",
    },
    effects: {
      loadingScreen: "playful",
      proposalAnimation: false,
      letterStyle: "playful",
    },
  },

  close_friend: {
    id: "template-friend-close",
    recipientType: "close_friend",
    archetype: "heartbeat",
    name: "Dear Friend",
    description: "Show appreciation for a close friendship",
    thumbnail:
      "https://images.pexels.com/photos/1683903/pexels-photo-1683903.jpeg",
    preview: {
      title: "Dear Friend",
      message: "Grateful for your friendship and all the memories.",
      personalNote: "You make life better just by being in it.",
      memory: "All the conversations that went late into the night.",
      insideJoke: "We have too many inside jokes to count. 😄",
    },
    theme: {
      id: "friend-warm",
      primaryColor: "#f78fb3",
      secondaryColor: "#f8a5c2",
      accentColor: "#f3a683",
      fontFamily: "Quicksand",
    },
    effects: {
      loadingScreen: "romantic",
      proposalAnimation: false,
      letterStyle: "heartfelt",
    },
  },

  friend_group: {
    id: "template-friend-group",
    recipientType: "friend_group",
    archetype: "moments",
    name: "The Squad",
    description: "Celebrate your friend group",
    thumbnail:
      "https://images.pexels.com/photos/7241938/pexels-photo-7241938.jpeg",
    preview: {
      title: "To My Squad",
      message: "Better together. Always and forever.",
      personalNote: "Each of you brings something special.",
      memory: "All the adventures, big and small.",
      insideJoke: "Group chat goes crazy. 📱",
    },
    theme: {
      id: "group-fun",
      primaryColor: "#f9ca24",
      secondaryColor: "#f6e58d",
      accentColor: "#ffb8b8",
      fontFamily: "Quicksand",
    },
    effects: {
      loadingScreen: "playful",
      proposalAnimation: false,
      letterStyle: "playful",
    },
  },

  long_distance_friend: {
    id: "template-friend-distance",
    recipientType: "long_distance_friend",
    archetype: "safehaven",
    name: "Close at Heart",
    description: "For friends far away",
    thumbnail:
      "https://images.pexels.com/photos/3944409/pexels-photo-3944409.jpeg",
    preview: {
      title: "Thinking of You",
      message: "Distance means so little when someone means so much.",
      personalNote: "Can't wait to see you again.",
      memory:
        "The last time we were together, laughing like no time had passed.",
      insideJoke: "Still waiting for our next video call marathon. 💻",
    },
    theme: {
      id: "distance-calm",
      primaryColor: "#a5b1c2",
      secondaryColor: "#778ca3",
      accentColor: "#4b6584",
      fontFamily: "Montserrat",
    },
    effects: {
      loadingScreen: "minimal",
      proposalAnimation: false,
      letterStyle: "poetic",
    },
  },

  // Self
  self_love: {
    id: "template-self-love",
    recipientType: "self_love",
    archetype: "heartbeat",
    name: "Treat Yourself",
    description: "Celebrate you",
    thumbnail:
      "https://images.pexels.com/photos/3962294/pexels-photo-3962294.jpeg",
    preview: {
      title: "To Myself",
      message: "You've been through so much and you're still standing.",
      personalNote: "You are enough. You always have been.",
      memory: "That time you thought you couldn't do it, but you did.",
      insideJoke: "Remember when you cried over that movie? Valid. 🎬",
    },
    theme: {
      id: "self-love-glow",
      primaryColor: "#ff9ff3",
      secondaryColor: "#feca57",
      accentColor: "#ff6b6b",
      fontFamily: "Quicksand",
    },
    effects: {
      loadingScreen: "romantic",
      proposalAnimation: false,
      letterStyle: "poetic",
    },
  },

  self_healing: {
    id: "template-self-healing",
    recipientType: "self_healing",
    archetype: "safehaven",
    name: "Healing Journey",
    description: "Honor your strength",
    thumbnail:
      "https://images.pexels.com/photos/356040/pexels-photo-356040.jpeg",
    preview: {
      title: "To My Healing Heart",
      message: "Healing isn't linear, but you keep showing up.",
      personalNote: "It's okay to not be okay.",
      memory: "The day you decided to choose yourself.",
      insideJoke: "Look at you now. 🌅",
    },
    theme: {
      id: "healing-calm",
      primaryColor: "#a5b1c2",
      secondaryColor: "#778ca3",
      accentColor: "#4b6584",
      fontFamily: "Georgia",
    },
    effects: {
      loadingScreen: "minimal",
      proposalAnimation: false,
      letterStyle: "heartfelt",
    },
  },

  self_celebration: {
    id: "template-self-celebration",
    recipientType: "self_celebration",
    archetype: "moments",
    name: "Personal Victory",
    description: "Celebrate your achievements",
    thumbnail:
      "https://images.pexels.com/photos/7249946/pexels-photo-7249946.jpeg",
    preview: {
      title: "You Did It!",
      message: "Celebrating you and everything you've accomplished.",
      personalNote: "This is just the beginning.",
      memory: "The moment you realized you could do it.",
      insideJoke: "Time to celebrate! 🎉",
    },
    theme: {
      id: "celebration-festive",
      primaryColor: "#f9ca24",
      secondaryColor: "#f6e58d",
      accentColor: "#ffb8b8",
      fontFamily: "Montserrat",
    },
    effects: {
      loadingScreen: "playful",
      proposalAnimation: false,
      letterStyle: "playful",
    },
  },
};

export const getTemplateByArchetype = (
  archetype: ArchetypeId,
): CardTemplate => {
  // Return a default template based on archetype
  switch (archetype) {
    case "moments":
      return CARD_TEMPLATES.self_celebration;
    case "heartbeat":
      return CARD_TEMPLATES.partner;
    case "safehaven":
      return CARD_TEMPLATES.self_healing;
    default:
      return CARD_TEMPLATES.partner;
  }
};

export const getTemplateByRecipientType = (
  recipientType: RecipientType,
  archetype?: ArchetypeId,
): CardTemplate => {
  return (
    CARD_TEMPLATES[recipientType] ||
    getTemplateByArchetype(archetype || "heartbeat")
  );
};
